import * as React from 'react'
import { CopyIcon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import Highlight, {
  Language,
  PrismTheme,
  defaultProps,
} from 'prism-react-renderer'
import { theme as defaultTheme } from './bshb-theme'
import { toast } from 'sonner'
import { IconButton } from './icon-button'
import { Icon } from 'basehub/react-icon'
import { ButtonLink } from './button'
import { ScrollArea } from './scroll-area'
// import { ScrollArea } from './scroll-area'

export type CodeSnippetProps = {
  code: string
  language: Language
  className?: string
  lineNumbers?: boolean
  unstyled?: boolean
  theme?: PrismTheme
  onCopy?: React.ClipboardEventHandler<HTMLPreElement>
  copyContentOverride?: string | (() => string)
}

export const CodeSnippetContainer = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={clsx(
        'w-full rounded-xl border border-dark-border-solid bg-dark-base overflow-hidden',
        className
      )}
    >
      {children}
    </div>
  )
}

export const CodeSnippetActions = ({
  label,
  icon,
  className,
  children,
}: {
  label?: React.ReactNode
  icon?: React.ReactNode
  className?: string
  children?: React.ReactNode
}) => {
  return (
    // <ScrollArea
    //   orientation="horizontal"
    //   variant="subtle"
    //   viewportClassName="!min-w-full"
    //   className={clsx(
    //     'flex items-center justify-between border-b border-dark-border-solid text-dark-faint bg-dark-base-dark w-full',
    //     className
    //   )}
    // >
    <span
      className={clsx(
        'flex items-center justify-between h-[37px] pl-3 pr-1 border-b border-dark-border-solid text-dark-faint bg-dark-base-dark',
        className
      )}
    >
      {icon}
      {!!label && <p className="ml-[5px] mr-auto text-xs leading-4">{label}</p>}
      <div className="flex items-center gap-x-4">{children}</div>
    </span>
    // </ScrollArea>
  )
}

export const CodeSnippetFooter = ({
  links,
}: {
  links: { label: string; href: string; icon?: string | React.ReactNode }[]
}) => {
  if (!links.length) return null

  return (
    <div className="flex items-center border-t border-dark-border-solid text-dark-muted h-9 bg-dark-base-dark">
      {links.map((link, index) => {
        return (
          <ButtonLink
            key={index}
            unstyled
            href={link.href}
            className="flex items-center text-xs focus-visible:-outline-offset-1 first:rounded-bl-xl font-medium gap-x-1.5 border-r border-dark-border-solid last:border-none px-4 rounded-none h-full hover:underline"
            target="_blank"
          >
            {typeof link.icon === 'string' ? (
              <Icon content={link.icon} />
            ) : (
              link.icon
            )}
            {link.label}
          </ButtonLink>
        )
      })}
    </div>
  )
}

export const CodeSnippet = ({
  code,
  language,
  className,
  lineNumbers = false,
  unstyled = false,
  theme = defaultTheme,
  onCopy,
  copyContentOverride,
}: CodeSnippetProps) => {
  return (
    <Highlight {...defaultProps} code={code} language={language} theme={theme}>
      {({ tokens, getLineProps, getTokenProps }) => (
        <div className="relative">
          <ScrollArea asChild variant="subtle" orientation="both">
            <pre
              className={clsx(
                'overflow-auto !font-mono text-xs',
                className,
                !unstyled &&
                  'rounded-lg border border-dark-border-solid bg-dark-shade light:bg-dark-base px-6 py-4'
              )}
              onCopy={onCopy}
            >
              {tokens.map((line, index) => {
                const { key, ...tokenProps } = getLineProps({
                  line,
                  key: index,
                })

                return (
                  <div key={key} {...tokenProps} className="mr-2 w-fit">
                    {lineNumbers && (
                      <span className="mr-[10px] pointer-events-none inline-block min-w-[16px] select-none text-dark-faint pl-1">
                        {index + 1}
                      </span>
                    )}

                    {line.map((token, _key) => {
                      const { key, ...tokenProps } = getTokenProps({
                        token,
                        key: _key,
                      })

                      return <span key={key} {...tokenProps} />
                    })}
                  </div>
                )
              })}
            </pre>
          </ScrollArea>

          <IconButton
            className="absolute top-2 right-2 p-2 text-dark-muted border border-dark-border-solid bg-dark-shade rounded"
            onClick={() => {
              navigator.clipboard.writeText(
                (typeof copyContentOverride === 'function'
                  ? copyContentOverride()
                  : copyContentOverride) ?? code
              )
              toast.success('Copied to clipboard')
            }}
          >
            <CopyIcon className="size-4 text-dark-bright" />
          </IconButton>
        </div>
      )}
    </Highlight>
  )
}
